import * as React from 'react'
import { connect } from 'react-redux'

import { strings } from '../../lib/localization'
import { emailTranscript, emailTranscriptResponse } from '../../actions/online'

import '../../sass/online/main.scss'
import '../../sass/online/email-transcript.scss'
import { RootState } from '../../types/rootstate'
import EscapeHandler from '../../lib/escape-handler'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface StateProps {}

interface DispatchProps {
  emailTranscript: (
    name: string,
    email: string
  ) => Promise<emailTranscriptResponse>
}

interface OwnProps {
  onClose: () => void
}

type EmailTranscriptProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState): StateProps {
  return {}
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    emailTranscript: (name, email) => dispatch(emailTranscript(name, email))
  }
}

interface EmailTranscriptState {
  name: string
  email: string
  errors: {
    nameError: boolean
    emailError: boolean
  }
  hasSent: boolean
}

class EmailTranscriptComponent extends React.Component<
  EmailTranscriptProps,
  EmailTranscriptState
> {
  references: {
    name: HTMLInputElement
    emailAddress: HTMLInputElement
  } = {
    name: null,
    emailAddress: null
  }

  constructor(props) {
    super(props)

    this.state = {
      name: '',
      email: '',
      errors: {
        nameError: false,
        emailError: false
      },
      hasSent: false
    }
  }

  componentDidMount() {
    setTimeout(() => {
      if (this.references.name) {
        this.references.name.focus()
      }
    })
    EscapeHandler.registerEscapeListener(this.handleEscape)
  }

  componentWillUnmount() {
    EscapeHandler.removeEscapeListener(this.handleEscape)
  }

  handleEscape = () => {
    this.props.onClose()
  }

  handleEmailKeyDown = (e) => {
    if (e.which === 13) {
      e.preventDefault()

      this.setState({
        errors: {
          ...this.state.errors,
          emailError: false
        }
      })

      if (this.validateEmail(this.state.email)) {
        this.props.emailTranscript(this.state.name, this.state.email)
        this.setState({
          hasSent: true
        })
      } else {
        this.setState({
          errors: {
            ...this.state.errors,
            emailError: true
          }
        })
      }
    }
  }

  handleNameKeyDown = (e) => {
    if (e.which === 13) {
      e.preventDefault()

      if (this.state.name !== '') {
        setTimeout(() => this.references.emailAddress.focus())
      }
    }
  }

  validateEmail = (email) => {
    // tslint:disable:max-line-length
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    // tslint:enable:max-line-length
    return re.test(email)
  }

  validateForm() {
    const hasName = this.state.name !== ''
    const hasValidEmail = this.validateEmail(this.state.email)

    this.setState({
      errors: {
        nameError: !hasName,
        emailError: !hasValidEmail
      }
    })

    return hasName && hasValidEmail
  }

  handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      name: e.target.value
    })
  }

  handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (this.validateEmail(this.state.email)) {
      this.setState({
        errors: {
          ...this.state.errors,
          emailError: false
        },
        email: e.target.value
      })
    } else {
      this.setState({
        email: e.target.value
      })
    }
  }

  doSubmit = () => {
    this.setState({
      errors: {
        nameError: false,
        emailError: false
      }
    })

    if (this.validateForm()) {
      this.props.emailTranscript(this.state.name, this.state.email)

      // We have to set the state to hasSent last to make sure
      // all event listeners has been run, if we don't do this
      // the HoC withoutsideClick will be run and will think the
      // click was outside since hasSent: true removes the button
      // from the DOM
      window.setTimeout(() => {
        this.setState({
          hasSent: true
        })
      })
    }
  }

  handleSubmitClick = (e: React.MouseEvent<HTMLInputElement>) => {
    this.doSubmit()
  }

  handleSubmitKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      this.doSubmit()
    }
  }

  render() {
    return (
      <div className='email-transcript'>
        <div className='email-transcript-container'>
          <div className='container'>
            <div className='row'>
              <div className='col-xs-12'>
                <div className='desc'>
                  {this.state.hasSent
                    ? strings.emailConversationSent
                    : strings.emailConversation}
                </div>
              </div>

              {!this.state.hasSent && (
                <div className='col-xs-12'>
                  <div className='input-line'>
                    <label htmlFor='name'>{strings.emailConversationName}</label>
                    <input
                      id='name'
                      type='text'
                      onChange={this.handleNameChange}
                      placeholder={strings.emailConversationNamePlaceholder}
                      onKeyDown={this.handleNameKeyDown}
                      ref={(node) => (this.references.name = node)}
                      aria-required
                      aria-invalid={!!this.state.errors.nameError}
                    />
                    {this.state.errors.nameError && (
                      <div className='input-error'>
                        {strings.emailConversationNameValidationError}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!this.state.hasSent && (
                <div className='col-xs-12'>
                  <div className='input-line'>
                    <label htmlFor='email'>{strings.emailConversationEmail}</label>
                    <input
                      id='email'
                      type='text'
                      onChange={this.handleEmailChange}
                      placeholder={strings.emailConversationEmailPlaceholder}
                      onKeyDown={this.handleEmailKeyDown}
                      ref={(node) => (this.references.emailAddress = node)}
                      aria-required
                      aria-invalid={!!this.state.errors.emailError}
                    />
                    {this.state.errors.emailError && (
                      <div className='input-error'>
                        {strings.emailConversationEmailValidationError}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!this.state.hasSent && (
                <div className='col-xs-12'>
                  <div className='button primary accentSecondary'>
                    <span
                      id='submitButton'
                      className='subBtn'
                      onClick={this.handleSubmitClick}
                      tabIndex={0}
                      role='button'
                      onKeyDown={this.handleSubmitKeyDown}
                    >
                      {strings.emailTranscriptSend}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

const EmailTranscript = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(EmailTranscriptComponent)

export { EmailTranscript }
